import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import CheckBox from 'components/CheckBox'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AccessLevel from 'components/Lists/AccessLevelList/AccessLevel'
import AddAccessLevelsModal from 'pages/Modals/AddAccessLevelsModal'
import ProfessionalPermissionsModal from 'pages/Modals/ProfessionalPermissionsModal'
import AlertDialog from 'components/AlertDialog'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Container, Column, CheckRow, Label } from '../../styles'

export default function AccessLevels ({
  professional,
  onValidationPermissionUpdated: handleValidationPermissionUpdated
}) {
  const [loading, setLoading] = useState (true)
  const [accessLevels, setAccessLevels] = useState ([])
  const [permissions, setPermissions] = useState ([])
  const [professionalAccessLevels, setProfessionalAccessLevels] = useState ([])
  const [professionalPermissions, setProfessionalPermissions] = useState ([])
  const [allowValidation, setAllowValidation] = useState (professional.allow_validation)
  const [showAddAccessLevelsModal, setShowAddAccessLevelsModal] = useState (false)
  const [showProfessionalPermissionsModal, setShowProfessionalPermissionsModal] = useState (false)
  const [showAllowValidationDialog, setShowAllowValidationDialog] = useState (false)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      const [
        { data: accessLevels },
        { data: professionalAccessLevels },
        { data: permissions },
        { data: professionalPermissions }
      ] = await Promise.all ([
        cutileiApi.get (`/access_levels?business_id=${businessId}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/professionals/${professional.id}/access_levels`, requestConfig),
        cutileiApi.get ('/permissions', requestConfig),
        cutileiApi.get (`/businesses/${businessId}/professionals/${professional.id}/permissions`, requestConfig)
      ])

      setAccessLevels (accessLevels)
      setProfessionalAccessLevels (professionalAccessLevels)
      setPermissions (permissions)
      setProfessionalPermissions (professionalPermissions)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const changeValidationPermission = async () => {
    try {
      const newValue = !allowValidation
      await cutileiApi.post (
        `/businesses/${businessId}/professionals/${professional.id}/access_levels`,
        { allow_validation: newValue },
        requestConfig
      )

      setAllowValidation (newValue)
      handleValidationPermissionUpdated ({
        ...professional, allow_validation: newValue
      })
    } catch (error) {
      logApiError (error)
    }
  }

  const toggleAddAccessLevelsModal = () => setShowAddAccessLevelsModal (!showAddAccessLevelsModal)
  const toggleAllowValidationDialog = () => setShowAllowValidationDialog (!showAllowValidationDialog)
  const toggleProfessionalPermissionsModal = () => {
    setShowProfessionalPermissionsModal (!showProfessionalPermissionsModal)
  }

  if (loading) return <Loading style={{marginTop: 30}}/>

  return (
    <Container>
      <SecondaryButton
        label='Atribuir níveis de acesso'
        onClick={toggleAddAccessLevelsModal}
        width={200}
      />
      {professionalAccessLevels.some (al => al.codename === 'service') && (
        <CheckRow>
          <CheckBox
            id='allow_validation'
            value={allowValidation}
            onValueChange={toggleAllowValidationDialog}
          />
          <Label htmlFor='allow_validation'>
            Permitir validação de QR Code
          </Label>
        </CheckRow>
      )}
      <Column>
        {professionalAccessLevels.map ((accessLevel, index) => (
          <AccessLevel
            key={accessLevel.id}
            index={index}
            data={accessLevel}
            professionalID={professional.id}
            enableEdit={true}
            professionalPermissions={professionalPermissions}
            onEdit={toggleProfessionalPermissionsModal}
            onDelete={accessLevel => {
              setProfessionalAccessLevels (professionalAccessLevels.filter (al => (
                al.id !== accessLevel.id
              )))
            }}
            style={{width: 840}}
          />
        ))}
      </Column>
      <AddAccessLevelsModal
        visible={showAddAccessLevelsModal}
        professional={professional}
        accessLevels={accessLevels}
        professionalAccessLevels={professionalAccessLevels}
        onConfirm={newAccessLevels => {
          setProfessionalAccessLevels ([...professionalAccessLevels, ...newAccessLevels])
        }}
        onClose={toggleAddAccessLevelsModal}
      />
      <ProfessionalPermissionsModal
        visible={showProfessionalPermissionsModal}
        professional={professional}
        accessLevels={accessLevels}
        permissions={permissions}
        professionalPermissions={professionalPermissions}
        onConfirm={setProfessionalPermissions}
        onClose={toggleProfessionalPermissionsModal}
      />
      <AlertDialog
        visible={showAllowValidationDialog}
        title='Atenção!'
        message={allowValidation
          ? 'Deseja realmente retirar a permissão para validar agendamentos Cutilei deste(a) profissional?'
          : 'Deseja realmente permitir que este(a) profissional valide agendamentos Cutilei por QR Code?'
        }
        confirmText='Sim'
        cancelText='Não'
        onConfirm={changeValidationPermission}
        onClose={toggleAllowValidationDialog}
        containerStyle={{width: 360}}
      />
    </Container>
  )
}
